'use client';

import { FC } from 'react';

import { Modal } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { NAVIGATION_MODAL_ZINDEX } from '@telialt/common-constants/src/modal';
import { ReadOnlyCard } from '@telialt/common-readonly-card/src/ReadOnlyCard';
import { RichTextContent } from '@telialt/common-rich-text-content/src/RichTextContent';
import { RichTextType } from '@telialt/models/src/CmsContent';

import { Gift } from '../model';
import { CardsList, PreviewModalBody } from './components/GiftSelection.styles';
import { PriceTag } from './components/PriceTag';
import { GIFT_SELECTION_TEST_ID } from './constants';
import { useGiftPreviewModalControls } from './hooks/useGiftPreviewModalControls';

type Props = { gifts: Gift[]; title: string; description?: RichTextType };

export const GiftPreviewModal: FC<Props> = ({ gifts, title, description }) => {
    const { isGiftPreviewModalOpen, closeGiftPreviewModal } = useGiftPreviewModalControls();
    const { t } = useTranslations();

    return (
        <Modal open={isGiftPreviewModalOpen} onOpenChange={(open) => !open && closeGiftPreviewModal()}>
            <Modal.Content
                title={title}
                stickyButtons={true}
                closeButtonAriaLabel={t['common.button.close']}
                showCloseButton={true}
                data-test={GIFT_SELECTION_TEST_ID.GIFT_PREVIEW_MODAL}
                zIndex={NAVIGATION_MODAL_ZINDEX}
            >
                <PreviewModalBody>
                    {!!description && <RichTextContent richText={description} />}
                    <CardsList role="list">
                        {gifts?.map((gift) => (
                            <ReadOnlyCard
                                key={gift.id}
                                title={gift.title}
                                image={
                                    gift.image?.src
                                        ? { src: gift.image.src, altText: gift.image.altText || '' }
                                        : undefined
                                }
                                body={<PriceTag price={gift.price} oldPrice={gift.oldPrice} />}
                                imagePosition="right"
                                role="listitem"
                            />
                        ))}
                    </CardsList>
                </PreviewModalBody>
            </Modal.Content>
        </Modal>
    );
};
